import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BookOpen, Download, ArrowRight, CheckCircle } from 'lucide-react'
import Seo from '../components/Seo'

const READING_PATH = '/the-first-step.pdf'

const ComingHomeToMe: React.FC = () => {
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [emailEnabled, setEmailEnabled] = useState<boolean | null>(null)

  useEffect(() => {
    fetch('/api/first-step')
      .then((r) => r.json())
      .then((data) => setEmailEnabled(Boolean(data.emailEnabled)))
      .catch(() => setEmailEnabled(false))
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.')
      return
    }

    setSubmitting(true)
    try {
      const response = await fetch('/api/first-step', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: firstName.trim(), email: email.trim() }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Something went wrong. Please try again.')
      }

      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-cream">
      <Seo
        title="The First Step: A Free Reading"
        description="A free reading from Cassie's memoir, Coming Home to Me — the Prologue and two chapters about the first step into the gym."
        noindex
      />

      <section className="bg-brand-navy text-white">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-xs font-semibold uppercase tracking-widest-xl text-brand-blush mb-4">
              A free reading from my memoir
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              The First Step
            </h1>
            <p className="text-lg text-brand-cream/80 italic mb-8">
              A free reading from Coming Home to Me
            </p>
            <p className="max-w-2xl mx-auto text-brand-cream/90 text-lg leading-relaxed mb-4">
              I sat in the gym parking lot for twenty-three minutes before I could make myself open the car door.
            </p>
            <p className="max-w-2xl mx-auto text-brand-cream/70 leading-relaxed mb-4">
              At the time, it felt like the longest walk of my life.
            </p>
            <p className="max-w-2xl mx-auto text-brand-cream/70 leading-relaxed mb-4">
              <em>The First Step</em> includes the Prologue and two chapters from my memoir, <em>Coming Home to Me</em>: <strong>The Parking Lot</strong> and <strong>The First Step</strong>.
            </p>
            <p className="max-w-2xl mx-auto text-brand-cream/70 leading-relaxed">
              If you've ever felt like everyone else knows what they're doing and you're the only one standing outside wondering whether you belong, I wrote this for you.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <a
                href={READING_PATH}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-teal text-white font-semibold hover:opacity-90 transition-opacity"
              >
                <BookOpen size={18} />
                Open the Reading
              </a>
              <a
                href={READING_PATH}
                download="The-First-Step-Cassie-Tach.pdf"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-brand-navy/20 text-brand-navy font-semibold hover:bg-white transition-colors"
              >
                <Download size={18} />
                Download the PDF
              </a>
            </div>

            <div className="rounded-2xl overflow-hidden border border-brand-navy/10 shadow-soft-lg bg-white hidden sm:block">
              <iframe
                src={`${READING_PATH}#view=FitH`}
                title="The First Step — a free reading"
                className="w-full"
                style={{ height: '80vh' }}
              />
            </div>
            <p className="sm:hidden text-center text-sm text-gray-500 mt-2">
              Use the buttons above to open or download the reading on your device.
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-brand-navy">
        <div className="container-max px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-brand-cream mb-4">
            Your starting point isn't a qualification test.
          </h2>
          <p className="text-brand-cream/70 mb-8 leading-relaxed">
            If you're ready to talk about yours, I'd love to hear where you are and where you want to go.
          </p>
          <Link
            to="/book-consultation?src=first-step"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-cream text-brand-navy font-semibold hover:opacity-90 transition-opacity"
          >
            Schedule Your Complimentary Consultation
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {emailEnabled && (
      <section className="py-14 sm:py-20 bg-brand-cream">
        <div className="container-max px-4 sm:px-6 lg:px-8 max-w-xl mx-auto">
          {submitted ? (
            <div className="text-center bg-white rounded-2xl border border-brand-navy/10 p-8">
              <CheckCircle className="mx-auto mb-4 text-brand-teal" size={36} />
              <h3 className="font-display text-xl font-semibold text-brand-navy mb-2">
                Thank you. Your reading is on its way to your inbox.
              </h3>
              <p className="text-gray-600">
                If you don't see it in a few minutes, check your spam or promotions folder. Take your time with it. You don't have to finish it today.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-brand-navy/10 p-8">
              <h3 className="font-display text-xl font-semibold text-brand-navy mb-1 text-center">
                Want a copy in your inbox?
              </h3>
              <p className="text-gray-500 text-sm text-center mb-6">
                Optional. I'll email you the reading, along with a few notes about what happened next.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="first_name" className="form-label">First name</label>
                  <input
                    id="first_name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-teal text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {submitting ? 'Sending…' : 'Email Me a Copy'}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  I'll also send you a few emails about what happened next and what this story taught me about starting before you feel ready. You can unsubscribe anytime.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>
      )}
    </div>
  )
}

export default ComingHomeToMe
